/* eslint-disable import/order */
import React, { ChangeEvent, useCallback, useRef, useState, useEffect } from "react";

import { YMaps } from '@pbe/react-yandex-maps';
/* eslint-disable max-len */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
  Drawer,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useAppDispatch } from "@app/store";
import useInfinitePagination from "@app/hooks/useInfinitePagination";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Link } from "react-router-dom";
import { useGetMarketsQuery } from "@modules/market";
import { useCategories } from "@app/hooks/useCategories";
import { PRIVATE_PAGES } from "@app/router/pages";
import {
  useDeleteAllMarksMutation,
  useDeleteMarkMutation,
  useImportPreviewMutation,
  useImportConfirmMutation,
  useLazyGetMarksQuery,
  useCreateMarkMutation,
  useUpdateMarkMutation,
} from "@modules/mark";
import { setMarks } from "@modules/mark/store/mark.slice";
import { ApiEndpoint, EXTRA_DATE_FORMAT_WITH_TIME } from "@constants/index";
import DeleteButton from "common/DeleteButton";
import { format } from 'date-fns';
import { IMark, MapMarkImportResult } from "@modules/mark/types";

import MarksMap from "./components/MarksMap";
import MarkDrawer from "./components/MarkDrawer";
import ImportPreviewDialog from "./components/ImportPreviewDialog";

const MARKS_PAGE_SIZE = 100;

const Marks = () => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [getMarks] = useLazyGetMarksQuery();
  const [deleteMarks] = useDeleteMarkMutation();
  const [deleteAllMarks, { isLoading: isDeleteAllLoading }] = useDeleteAllMarksMutation();
  const [importPreview, { isLoading: isPreviewLoading }] = useImportPreviewMutation();
  const [importConfirm, { isLoading: isConfirmingLoading }] = useImportConfirmMutation();
  const [createMark] = useCreateMarkMutation();
  const [updateMark] = useUpdateMarkMutation();

  const [viewMode, setViewMode] = useState<"map" | "list">("map");

  // Filter states
  const [filterMarketId, setFilterMarketId] = useState<string>('');
  const [filterCategoryId, setFilterCategoryId] = useState<string>('');
  const [filterVisible, setFilterVisible] = useState<string>(''); // '' | 'true' | 'false'
  const [filterAddress, setFilterAddress] = useState<string>('');
  const [debouncedAddress, setDebouncedAddress] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAddress(filterAddress);
    }, 350);

    return () => clearTimeout(timer);
  }, [filterAddress]);

  const { data: marketsData } = useGetMarketsQuery({ limit: 100, offset: 0 });
  const markets = marketsData?.markets || [];
  const { categories } = useCategories();

  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMark, setSelectedMark] = useState<IMark | null>(null);
  const [newLocation, setNewLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | undefined>(undefined);

  // Bounds for map filtering
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);

  // Import preview states
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [importError, setImportError] = useState<string>("");

  const fetchMarksPage = useCallback(async ({ offset, limit }: { offset: number; limit: number }) => {
    // If in map mode and bounds exist, fetch by bounds
    const params: any = { offset, limit };

    if (filterMarketId) {
      params.marketId = filterMarketId;
    }

    if (filterCategoryId) {
      params.categoryId = filterCategoryId;
    }

    if (filterVisible !== '') {
      params.visible = filterVisible === 'true';
    }

    if (debouncedAddress.trim()) {
      params.address = debouncedAddress.trim();
    }

    if (viewMode === 'map' && mapBounds) {
      params.north = mapBounds.north;
      params.south = mapBounds.south;
      params.east = mapBounds.east;
      params.west = mapBounds.west;
    }

    const { marks, count } = await getMarks(params).unwrap();

    return { items: marks, count };
  }, [getMarks, viewMode, mapBounds, filterMarketId, filterCategoryId, filterVisible, debouncedAddress]);

  const {
    items: marksList,
    totalCount,
    isLoading: isLoadingMore,
    loadMoreRef,
    refresh,
  } = useInfinitePagination<IMark>({
    fetchPage: fetchMarksPage,
    pageSize: MARKS_PAGE_SIZE,
    onItemsChange: (marks) => dispatch(setMarks(marks)),
    onError: (error) => console.log(error),
  });

  // Re-fetch when map bounds change in map mode or filters change
  useEffect(() => {
    refresh();
  }, [
    mapBounds,
    viewMode,
    filterMarketId,
    filterCategoryId,
    filterVisible,
    debouncedAddress
  ]); // refresh is stable

  const handleDeleteAllMarks = async () => {
    setImportError("");
    try {
      await deleteAllMarks().unwrap();
      refresh();
    } catch (error) {
      setImportError("Failed to delete all map marks");
    }
  };

  const handleDeleteMark = async (id: string) => {
    if (!id) {
      return null;
    }

    try {
      await deleteMarks({ id });
      refresh();
    } catch (error) {
      console.log(error);
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      return;
    }

    setImportError("");
    try {
      const response = await importPreview(file).unwrap();

      setPreviewData(response.preview);
      setIsPreviewDialogOpen(true);
    } catch (error: any) {
      setImportError(error?.data?.message || "Failed to preview map marks");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConfirmImport = async () => {
    try {
      const validMarks = previewData.filter((d) => d.valid);

      await importConfirm({ marks: validMarks }).unwrap();
      setIsPreviewDialogOpen(false);
      refresh();
    } catch (error: any) {
      setImportError(error?.data?.message || "Failed to confirm import");
      setIsPreviewDialogOpen(false);
    }
  };

  const handleSaveMark = async (data: any) => {
    try {
      if (selectedMark) {
        await updateMark({ id: selectedMark.id, body: data }).unwrap();
      } else {
        await createMark(data).unwrap();
      }

      setDrawerOpen(false);
      refresh();
    } catch (error) {
      console.log(error);
    }
  };

  const renderCategories = (mark: IMark) => {
    if (mark.categories && mark.categories.length > 0) {
      return mark.categories
        .map((c: any) => c.titleEn || c.titleAm || c.title || c.nameEn || c.nameAm)
        .filter(Boolean)
        .join(', ');
    }

    return (
      [
        mark.iceCream && 'Ice Cream',
        mark.pastry && 'Pastry',
        mark.pudding && 'Pudding',
      ]
        .filter(Boolean)
        .join(' + ') || 'N/A'
    );
  };

  return (
    <YMaps query={{ apikey: process.env.REACT_APP_YANDEX_MAPS_API_KEY, lang: 'ru_RU' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', p: 2, gap: 2 }}>
        <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="map">Map View</ToggleButton>
            <ToggleButton value="list">List View</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedMark(null);
              setNewLocation(null);
              setDrawerOpen(true);
            }}
          >
            Add Mark
          </Button>

          <Button variant="outlined" component="label">
            {isPreviewLoading ? "Parsing..." : "Import via Excel"}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".csv, .xlsx, .xls"
              onChange={handleFileChange}
            />
          </Button>

          <DeleteButton
            text={isDeleteAllLoading ? "Deleting..." : "Delete all marks"}
            confirmationText="Do you really want to delete all marks?"
            buttonVariant="outlined"
            buttonColor="error"
            disabled={!totalCount || isDeleteAllLoading}
            callBack={handleDeleteAllMarks}
          />
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', p: 1, border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mr: 2 }}>Filters:</Typography>

          <TextField
            size="small"
            label="Search Address"
            placeholder="Search by address..."
            value={filterAddress}
            onChange={(e) => setFilterAddress(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: filterAddress ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilterAddress('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ minWidth: 240 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Market</InputLabel>
            <Select
              value={filterMarketId}
              label="Market"
              onChange={(e) => setFilterMarketId(e.target.value)}
            >
              <MenuItem value=""><em>All Markets</em></MenuItem>
              {markets.map((m: any) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                  {' '}
                  {!m.visible ? ' (Hidden on site)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filterCategoryId}
              label="Category"
              onChange={(e) => setFilterCategoryId(e.target.value)}
            >
              <MenuItem value=""><em>All Categories</em></MenuItem>
              {categories.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.titleEn || c.titleAm || c.title || c.nameEn || c.nameAm}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              value={filterVisible}
              label="Visibility"
              onChange={(e) => setFilterVisible(e.target.value)}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              <MenuItem value="true">Visible</MenuItem>
              <MenuItem value="false">Hidden</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {importError && (
          <Alert severity="error">{importError}</Alert>
        )}

        <Box sx={{ flexGrow: 1, display: 'flex', gap: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              flexGrow: 1,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #ccc',
              display: viewMode === 'map' ? 'block' : 'none',
              height: '100%',
            }}
          >
            <MarksMap
              center={mapCenter}
              marks={marksList}
              isVisible={viewMode === 'map'}
              onBoundsChanged={(bounds) => setMapBounds(bounds)}
              onMarkerClick={(mark) => {
                setSelectedMark(mark);
                setNewLocation(null);
                setMapCenter({ lat: parseFloat(mark.latitude), lng: parseFloat(mark.longitude) });
                setDrawerOpen(true);
              }}
              onMapClick={(lat, lng) => {
                setNewLocation({ lat, lng });
                setMapCenter({ lat, lng });
                setSelectedMark(null);
                setDrawerOpen(true);
              }}
            />
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              display: viewMode === 'list' ? 'block' : 'none',
              height: '100%',
            }}
          >
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Address</TableCell>
                    <TableCell align="center">Coordinates</TableCell>
                    <TableCell align="center">Categories</TableCell>
                    <TableCell align="center">Market</TableCell>
                    <TableCell align="center">Visible</TableCell>
                    <TableCell align="center">Action</TableCell>
                    <TableCell align="center">Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {marksList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                          {debouncedAddress ? `No marks found matching "${debouncedAddress}"` : "No marks found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {marksList.map((markItem) => (
                    <TableRow key={markItem.id}>
                      <TableCell align="center">{markItem.address || 'N/A'}</TableCell>
                      <TableCell align="center">
                        {`${markItem.latitude}, ${markItem.longitude}`}
                      </TableCell>
                      <TableCell align="center">
                        {renderCategories(markItem)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          {markItem.market && markItem.market.images?.[0]?.url && (
                            <img
                              width={40}
                              height={40}
                              style={{ objectFit: 'contain' }}
                              alt={markItem.market.name}
                              src={`${ApiEndpoint}/${markItem.market.images[0].url}`}
                            />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {markItem.market?.name || 'N/A'}
                          </Typography>
                          {markItem.market && !markItem.market.visible && (
                            <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                              (Hidden on site)
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">{markItem.visible ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedMark(markItem);
                            setNewLocation(null);
                            setMapCenter({ lat: parseFloat(markItem.latitude), lng: parseFloat(markItem.longitude) });
                            setDrawerOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <DeleteButton callBack={() => handleDeleteMark(markItem.id)} />
                      </TableCell>
                      <TableCell align="center">
                        {format(new Date(markItem.createdAt), EXTRA_DATE_FORMAT_WITH_TIME)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box ref={loadMoreRef} sx={{ display: "flex", justifyContent: "center", minHeight: 40, mt: 2 }}>
              {isLoadingMore && <CircularProgress size={24} />}
            </Box>
          </Box>
        </Box>

        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 400 }}>
            {drawerOpen && (
              <MarkDrawer
                mark={selectedMark}
                newLocation={newLocation}
                onClose={() => setDrawerOpen(false)}
                onSave={handleSaveMark}
                onDelete={async (id: string) => {
                  await handleDeleteMark(id);
                  setDrawerOpen(false);
                }}
                onLocationSelect={(lat, lng) => setMapCenter({ lat, lng })}
              />
            )}
          </Box>
        </Drawer>

        <ImportPreviewDialog
          open={isPreviewDialogOpen}
          previewData={previewData}
          onConfirm={handleConfirmImport}
          onCancel={() => setIsPreviewDialogOpen(false)}
          isConfirming={isConfirmingLoading}
        />
      </Box>
    </YMaps>
  );
};

export default Marks;
